import axiosInstance from '../utils/api';

/**
 * Execute DIY Agent with streaming
 * @param {Object} params - Parameters for the API call
 * @param {string} params.sessionId - Session ID
 * @param {string} params.fourayAgentId - Agent ID
 * @param {string} params.query - Query/prompt text
 * @param {Object} params.variables - Variables object (will be JSON stringified)
 * @param {boolean} params.streaming - Whether to stream the response
 * @param {Function} params.onChunk - Callback for each chunk
 * @param {Function} params.onComplete - Callback when streaming is complete
 * @param {Function} params.onError - Callback for errors
 */
export const executeDiyAgent = async ({
  sessionId,
  fourayAgentId,
  query = '',
  variables = {},
  streaming = true,
  onChunk,
  onComplete,
  onError,
}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      if (onError) onError(error);
      throw error;
    }

    // Build variables object - only include non-empty values
    const variablesObj = {};
    Object.keys(variables).forEach(key => {
      const value = variables[key];
      // Skip null, undefined, empty string, and empty objects
      if (value !== null && value !== undefined && value !== '') {
        // Handle select objects (language, locationFocus, etc.)
        if (typeof value === 'object' && value.value !== undefined) {
          variablesObj[key] = value.value;
        } else {
          variablesObj[key] = value;
        }
      }
    });

    // Build FormData - matching curl example format
    const formData = new FormData();
    formData.append('agent_id', fourayAgentId.toString());
    
    // Add query if provided
    if (query) {
      formData.append('query', query);
    }
    
    formData.append('session_id', sessionId);
    formData.append('streaming', streaming.toString());

    // Always add variables, even if empty (pass null if no variables)
    const variablesString = Object.keys(variablesObj).length > 0 
      ? JSON.stringify(variablesObj) 
      : 'null';
    formData.append('variables', variablesString);

    // Use fetch for streaming support
    const response = await fetch(`${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`, {
      method: 'POST',
      headers: {
        Authorization: token,
      },
      body: formData
    //  body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        const error = new Error('Authentication failed. Please log in again.');
        if (onError) onError(error);
        throw error;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = "";
    let jsonBuffer = ""; // Buffer to accumulate JSON fragments
    let finalJsonString = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          
          // Skip empty data lines
          if (!data || data.trim() === '') {
            continue;
          }
          
          if (data === '[DONE]') {
            // Try to parse accumulated JSON before completing
            if (jsonBuffer) {
              try {
                const parsed = JSON.parse(jsonBuffer);
                // Extract content from various response structures
                let contentToExtract = '';
                if (parsed.freeform_response && parsed.freeform_response.response) {
                  contentToExtract = parsed.freeform_response.response;
                } else if (parsed.blog_response) {
                  // For blog, we don't extract string content - the whole structure is needed
                  // Just mark that we have blog content
                  contentToExtract = ''; // Blog content is structured, not a string
                } else if (parsed.response) {
                  contentToExtract = parsed.response;
                } else if (parsed.content) {
                  contentToExtract = parsed.content;
                }
                
                if (contentToExtract) {
                  accumulatedContent = contentToExtract;
                  finalJsonString = jsonBuffer;
                }
              } catch (e) {
                // If parsing fails, use accumulated content as-is
              }
            }
            
            if (onComplete) {
              onComplete(accumulatedContent, finalJsonString);
            }
            return;
          }

          // Skip metadata chunks (run_meta, etc.)
          if (data.includes('"type"') && (data.includes('run_meta') || data.includes('agent_id'))) {
            continue;
          }

          // Accumulate JSON fragments
          jsonBuffer += data;

          // Try to parse the accumulated JSON buffer
          let parsed = null;
          try {
            parsed = JSON.parse(jsonBuffer);
            // If parsing succeeds, we have complete JSON
            finalJsonString = jsonBuffer;
            
            // Extract content from various response structures
            let contentToAdd = '';
            if (parsed.freeform_response && parsed.freeform_response.response) {
              contentToAdd = parsed.freeform_response.response;
            } else if (parsed.blog_response) {
              // For blog, we don't extract string content - the whole structure is needed
              // Pass the JSON string so the UI can detect and render it
              if (onChunk) {
                // For blog, pass the JSON string as accumulatedContent for detection
                onChunk('', jsonBuffer); // Pass empty chunk but JSON string for detection
              }
            } else if (parsed.email_response || parsed.email_sections) {
              // For email, we don't extract string content - the whole structure is needed
              // Pass the JSON string so the UI can detect and render it
              if (onChunk) {
                // For email, pass the JSON string as accumulatedContent for detection
                onChunk('', jsonBuffer); // Pass empty chunk but JSON string for detection
              }
            } else if (parsed.response) {
              contentToAdd = parsed.response;
            } else if (parsed.content) {
              contentToAdd = parsed.content;
            }
            
            // If we found string content, extract it and stream it
            if (contentToAdd && typeof contentToAdd === 'string') {
              // Only add new content that hasn't been added yet
              const newContent = contentToAdd.substring(accumulatedContent.length);
              if (newContent) {
                accumulatedContent = contentToAdd;
                if (onChunk) {
                  onChunk(newContent, accumulatedContent);
                }
              }
            }
          } catch (e) {
            // JSON is incomplete, continue accumulating
            // Don't do anything, just keep building the buffer
          }
        }
      }
    }

    // Try to parse final accumulated JSON
    if (jsonBuffer && !finalJsonString) {
      try {
        const parsed = JSON.parse(jsonBuffer);
        finalJsonString = jsonBuffer;
        
        // Extract content from various response structures
        let contentToExtract = '';
        if (parsed.freeform_response && parsed.freeform_response.response) {
          contentToExtract = parsed.freeform_response.response;
        } else if (parsed.blog_response) {
          // For blog, we don't extract string content - the whole structure is needed
          contentToExtract = ''; // Blog uses structured data
        } else if (parsed.response) {
          contentToExtract = parsed.response;
        } else if (parsed.content) {
          contentToExtract = parsed.content;
        }
        
        if (contentToExtract) {
          accumulatedContent = contentToExtract;
        }
      } catch (e) {
        // If parsing still fails, use accumulated buffer as-is
        accumulatedContent = jsonBuffer;
      }
    }

    // Call onComplete if streaming finished without [DONE]
    if (onComplete) {
      onComplete(accumulatedContent, finalJsonString || jsonBuffer);
    }
  } catch (error) {
    console.error('Error executing DIY agent:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Upload files to Fouray knowledge base
 * @param {File[]} files - Array of files to upload
 * @param {string|number} folderId - Folder ID (dim3_folder_id) for the knowledge base
 * @returns {Promise<Object>} - Response data from the API
 */
export const uploadFilesToFouray = async (files, folderId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    if (!files || files.length === 0) {
      throw new Error('No files provided for upload');
    }

    if (!folderId) {
      throw new Error('Folder ID is required for upload');
    }

    // Create FormData for fouray upload API
    const formData = new FormData();
    
    // Append files
    files.forEach((file) => {
      formData.append("files[]", file);
    });
    
    // Append folder ID
    formData.append("dim3_folder_id", folderId.toString());

    // Call fouray upload API
    const response = await axiosInstance.post("/api/v1/fouray/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error uploading files to Fouray:', error);
    throw error;
  }
};

export default executeDiyAgent;

